import * as Sentry from '@sentry/react';
import { redactor } from '../redactor/redactor';
import { createSentryIntegrations } from '@acorn/observability';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';
import { useEffect } from 'react';

type SentryEnv = 'Local' | 'Development' | 'Staging' | 'Production';
const sentryEnv: SentryEnv = (import.meta.env.VITE_ENV as SentryEnv) ?? 'Local';
const sentryDsn: string = import.meta.env.VITE_SENTRY_DSN ?? '';
const journey: string = import.meta.env.VITE_JOURNEY ?? '';

// Trace sampling per env: full in non-prod, sampled in prod, off locally.
const traceSampleRates: Record<SentryEnv, number> = {
	Local: 0.0,
	Development: 1.0,
	Staging: 1.0,
	Production: 0.2,
};

// Attach trace headers only to our own origin (deployed APIs are same-origin under portal.<domain>).
const getTracePropagationTargets = (): (string | RegExp)[] => {
	if (sentryEnv === 'Local') {
		return [/localhost/];
	}

	// Brand from journey, e.g. "Acorn-portal-dev" -> "acorn"
	const brand = journey.toLowerCase().split('-')[0];
	const brandDomains: Record<string, string> = {
		acorn: 'acorninsure.co.uk',
		carrot: 'carrotinsurance.com',
		flag: 'flaginsurance.co.uk',
		motorcade: 'motorcade.co.uk',
		mypolicy: 'mypolicy.co.uk',
		streetcover: 'streetcover.co.uk',
	};

	const domain = brandDomains[brand];
	if (!domain) return [];

	const hostByEnv: Partial<Record<SentryEnv, string>> = {
		Production: `portal.${domain}`,
		Staging: `portal.staging-${domain}`,
		Development: `portal.dev-${domain}`,
	};
	const host = hostByEnv[sentryEnv];
	if (!host) return [];

	// Escape dots, then anchor with (/|$) so look-alikes (…co.uk.evil.com) can't match.
	const escapedHost = host.split('.').join(String.raw`\.`);
	return [new RegExp(`^https://${escapedHost}(/|$)`)];
};

Sentry.init({
	dsn: sentryDsn,
	environment: sentryEnv,
	release: import.meta.env.VITE_RELEASE ?? 'unknown',
	// Off so Sentry doesn't attach IP/headers (not covered by the redactor globs).
	sendDefaultPii: false,
	integrations: [
		// Must be first so PII is stripped before other integrations see the event.
		createSentryIntegrations(redactor, {
			processStackTraces: true,
		}),
		// Router tracing — don't also add browserTracingIntegration() (it would override this).
		Sentry.reactRouterBrowserTracingIntegration({
			useEffect,
			useLocation,
			useNavigationType,
			createRoutesFromChildren,
			matchRoutes,
		}),
	],
	debug: false,
	tracePropagationTargets: getTracePropagationTargets(),
	tracesSampleRate: traceSampleRates[sentryEnv],
	normalizeDepth: 4,
});
